/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { Flag } from "@superweb/flags";
import {
  DateField,
  ExperimentalFormRow,
  TextField,
  type ComboBoxOption,
  type ComboBoxState,
  type DateFieldState,
  type TextFieldState,
} from "@superweb/ui";

import { useMessage } from "#internal/intl";
import { CountrySuggest } from "#internal/ui";
import { useErrorMessages, useValidate } from "#internal/utils";

export type DriverLicenseDetailsState = {
  country: ComboBoxState<ComboBoxOption>;
  number: TextFieldState;
  issue_date: DateFieldState;
  expiry_date: DateFieldState;
};

export const DriverLicenseDetailsFields = ({
  state,
  onChangePartial,
  countryCode,
  countryId,
  licenseTemplates,
}: {
  state: DriverLicenseDetailsState;
  onChangePartial: (
    partialNextState: Partial<DriverLicenseDetailsState>,
  ) => void;
  countryCode: string;
  countryId: string;
  licenseTemplates: string[];
}) => {
  const message = useMessage();
  const errorMessages = useErrorMessages();

  const { validateTextField, validateDateField } = useValidate();

  return (
    <>
      <Flag
        id="15167e81-cd28-4b1d-a31a-bca6e73739c4"
        description="Enable driver license country selection"
      >
        <CountrySuggest
          label={message({
            id: "084d2158-7c5e-4666-b15e-3ec22dd8b4f5",
            context:
              "Fleet forms. Driver license details. Driver license country. Label",
            default: "Country of issue",
          })}
          countryId={countryId}
          state={state.country}
          onChange={(newState) => {
            if (
              ((licenseTemplates.length > 0 &&
                state.number.errorMessage ===
                  errorMessages.invalidDriverLicenseWithTemplates(
                    licenseTemplates.join(", "),
                  )) ||
                state.number.errorMessage ===
                  errorMessages.invalidDriverLicense) &&
              state.country.value !== newState.value
            ) {
              onChangePartial({
                country: newState,
                number: {
                  ...state.number,
                  errorMessage: undefined,
                  errorVisible: false,
                },
              });
            } else {
              onChangePartial({
                country: newState,
              });
            }
          }}
        />
      </Flag>
      <TextField
        required
        label={
          countryCode === "RU"
            ? message({
                id: "64d8cf10-7909-442f-b071-02b0c3d39424",
                context: "Yango forms. Driver license",
                default: "Driver's license series and number",
              })
            : message({
                id: "9af5b9d9-349e-4349-a008-281706c24270",
                context: "Yango forms. Driver license",
                default: "Driver's license number",
              })
        }
        state={state.number}
        onChange={(newState) => {
          const errorMessage = validateTextField(newState.value);
          onChangePartial({
            number: {
              ...newState,
              errorMessage,
              errorVisible: !!errorMessage,
            },
          });
        }}
      />
      <ExperimentalFormRow columnsCount={2}>
        <DateField
          label={message({
            id: "02d879f9-1327-46de-acb2-81eee55f38f5",
            context: "Yango forms. Date of issue",
            default: "Issue date",
          })}
          state={state.issue_date}
          onChange={(newState) => {
            const errorMessage = validateDateField(newState.value);
            onChangePartial({
              issue_date: {
                ...newState,
                errorMessage,
                errorVisible: !!errorMessage,
              },
            });
          }}
        />
        <DateField
          label={message({
            id: "776ba0e3-99e1-43be-ae7a-138b89219812",
            context: "Yango forms. Date of expire",
            default: "Expiration date",
          })}
          state={state.expiry_date}
          onChange={(newState) => {
            const errorMessage = validateDateField(newState.value);
            onChangePartial({
              expiry_date: {
                ...newState,
                errorMessage,
                errorVisible: !!errorMessage,
              },
            });
          }}
        />
      </ExperimentalFormRow>
    </>
  );
};
